import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useAuth, useLogout } from "@/lib/auth";
import { useBranding } from "@/hooks/use-branding";
import { Check, ShieldCheck, LogOut, Sparkles, Loader2 } from "lucide-react";
import { planDisplayFeatures } from "@/lib/plan-features";

type Plan = {
  id: string;
  name: string;
  slug: string;
  price: number;
  botLimit: number;
  agentLimit: number;
  monthlyMessages: number;
  features: string[];
  isActive: boolean;
  billingInterval: string;
  stripePriceId?: string | null;
  trialPeriodDays?: number | null;
};

/**
 * "Truly free" = $0 AND no Stripe price configured.
 * Plans with a StripePriceId always go through Stripe checkout — even if price
 * is $0 — because Stripe manages the trial period and future billing.
 */
const isFree = (plan: Plan) => plan.price === 0 && !plan.stripePriceId;

/** Plans with a trial period go through Stripe checkout but collect card for future billing. */
const isTrial = (plan: Plan) =>
  !!plan.stripePriceId && !!plan.trialPeriodDays && plan.trialPeriodDays > 0;

export default function BillingSelectPlanPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user, organization } = useAuth();
  const branding = useBranding();
  const logout = useLogout();
  const queryClient = useQueryClient();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  const { data: plans, isLoading } = useQuery<Plan[]>({
    queryKey: ["/api/subscription-plans"],
  });

  /** Activate a free ($0) plan directly — no Stripe checkout required. */
  async function activateFree(plan: Plan) {
    if (pendingId) return;
    setPendingId(plan.id);
    try {
      const res = await apiRequest("POST", "/api/billing/activate-free", { planId: plan.id });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || "Could not activate the free plan. Please try again.");
      }
      // Bust the auth/me cache so the new plan features and status propagate
      // to the rest of the app without requiring a full page reload.
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      setLocation("/");
    } catch (err: any) {
      toast({
        title: "Could not activate plan",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
      setPendingId(null);
    }
  }

  /** Start Stripe checkout for a paid plan. */
  async function subscribe(plan: Plan) {
    if (pendingId) return;
    setPendingId(plan.id);
    try {
      const origin = window.location.origin;
      const res = await apiRequest("POST", "/api/billing/subscribe", {
        planId: plan.id,
        successUrl: `${origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/billing/select-plan`,
      });
      const body = await res.json();
      if (body?.url) {
        window.location.href = body.url;
      } else {
        throw new Error("Stripe did not return a checkout URL.");
      }
    } catch (err: any) {
      toast({
        title: "Could not start checkout",
        description: err?.message || "Stripe may not be configured yet. Try again shortly.",
        variant: "destructive",
      });
      setPendingId(null);
    }
  }

  function handlePlanAction(plan: Plan) {
    if (isFree(plan)) {
      // Truly free plan (no Stripe price) — bypass Stripe entirely.
      activateFree(plan);
    } else {
      // Stripe-managed: paid plans AND free-trial plans (stripePriceId set, price may be $0).
      subscribe(plan);
    }
  }

  const activePlans = (plans || []).filter((p) => p.isActive).sort((a, b) => a.price - b.price);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <header className="flex items-center justify-between px-6 sm:px-10 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-sky-500/20 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-sky-400" />
          </div>
          <div>
            <div className="text-sm font-semibold">{branding.productName}</div>
            <div className="text-xs text-white/50" data-testid="text-billing-org">
              {organization?.name || user?.email}
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => logout.mutate()}
          className="text-white/70 hover:text-white"
          data-testid="button-billing-logout"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-12">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-sky-400 text-xs uppercase tracking-widest mb-3">
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" /> Final step
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold">Activate your workspace</h1>
          <p className="text-white/60 mt-3">
            Pick a plan to unlock chatbots, channels, and live agents. You can change or cancel anytime in the billing portal.
          </p>
        </div>

        {/* Pending-plan banner: shown when the org was provisioned with a
            paid plan by a Super Admin but payment hasn't been completed. */}
        {(organization as any)?.subscriptionStatus === "pending" &&
          (organization as any)?.plan &&
          (organization as any)?.plan !== "starter" && (
          <div
            role="status"
            className="max-w-2xl mx-auto mb-8 px-5 py-4 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-300 text-sm text-center"
            data-testid="banner-payment-pending"
          >
            Your{" "}
            <strong className="font-semibold capitalize">
              {(organization as any).plan.replace(/-/g, " ")}
            </strong>{" "}
            subscription payment is pending. Complete payment below to access your workspace.
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-20 text-white/60">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading plans…
          </div>
        )}

        {!isLoading && activePlans.length === 0 && (
          <Card className="bg-white/5 border-white/10 text-white max-w-xl mx-auto">
            <CardContent className="py-8 text-center text-white/70">
              No plans are currently published. Please contact your platform administrator.
            </CardContent>
          </Card>
        )}

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {activePlans.map((plan) => {
            const free = isFree(plan);
            return (
              <Card
                key={plan.id}
                data-testid={`card-plan-${plan.slug}`}
                onClick={() => setSelectedPlanId(plan.id)}
                className={[
                  "relative bg-white/5 border-white/10 text-white flex flex-col cursor-pointer transition-all duration-150",
                  plan.id === selectedPlanId
                    ? "ring-2 ring-sky-400 bg-sky-500/10 shadow-lg shadow-sky-500/10"
                    : "hover:bg-white/8 hover:ring-1 hover:ring-white/25",
                ].join(" ")}
              >
                <CardHeader>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription className="text-white/60 min-h-10">
                    Up to {plan.botLimit} bots
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="mb-6">
                    {free ? (
                      <span
                        className="text-4xl font-bold"
                        data-testid={`text-price-${plan.slug}`}
                      >
                        Free
                      </span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold" data-testid={`text-price-${plan.slug}`}>
                          ${plan.price}
                        </span>
                        <span className="text-white/50 text-sm">/{plan.billingInterval || "month"}</span>
                      </>
                    )}
                  </div>
                  <ul
                    className="space-y-2 text-sm text-white/80 mb-6 h-64 shrink-0 overflow-y-auto overflow-x-hidden pr-1"
                    data-testid={`features-plan-${plan.slug}`}
                  >
                    {planDisplayFeatures(plan).map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={(e) => { e.stopPropagation(); handlePlanAction(plan); }}
                    style={{ marginTop: "auto" }}
                    disabled={pendingId !== null}
                    data-testid={`button-subscribe-${plan.slug}`}
                    className={
                      plan.id === selectedPlanId
                        ? "w-full bg-sky-500 hover:bg-sky-400 shadow-md"
                        : "w-full bg-white/10 hover:bg-white/20"
                    }
                  >
                    {pendingId === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {free ? "Activating…" : "Redirecting…"}
                      </>
                    ) : (
                      free ? "Get Started"
                        : isTrial(plan) ? "Start Free Trial"
                        : "Subscribe"
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-xs text-white/40 mt-10">
          Payments are processed securely by Stripe. We never store your card details.
        </p>
      </main>
    </div>
  );
}
